import { useState, useEffect } from "react";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import "./Admin.scss";

export default function Admin() {
  // Form state
  const [form, setForm] = useState({
    name: "",
    price: "",
    description: "",
    category: "",
    subcategory: "",
    sizes: "",
    images: "",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [products, setProducts] = useState([]);
  const [editId, setEditId] = useState(null);

  // Load products on mount
  useEffect(() => {
    const fetchProducts = async () => {
      const querySnapshot = await getDocs(collection(db, "products"));
      const items = querySnapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setProducts(items);
    };
    fetchProducts();
  }, []);

  // Handle form changes
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Submit (add or update product)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      // Convert GitHub URLs to raw
      const images = form.images.split(",").map((url) => {
        let cleanUrl = url.trim();
        if (cleanUrl.includes("github.com") && cleanUrl.includes("/blob/")) {
          cleanUrl = cleanUrl
            .replace("github.com", "raw.githubusercontent.com")
            .replace("/blob/", "/");
        }
        return cleanUrl;
      });

      if (editId) {
        // Update product
        const docRef = doc(db, "products", editId);
        await updateDoc(docRef, {
          name: form.name,
          price: Number(form.price),
          description: form.description,
          category: form.category,
          subcategory: form.category === "merch" ? form.subcategory : "",
          sizes: form.sizes ? form.sizes.split(",").map((s) => s.trim()) : [],
          images,
        });
        setMessage("Produkt byl aktualizován.");
      } else {
        // Add new product
        await addDoc(collection(db, "products"), {
          name: form.name,
          price: Number(form.price),
          description: form.description,
          category: form.category,
          subcategory: form.category === "merch" ? form.subcategory : "",
          sizes: form.sizes ? form.sizes.split(",").map((s) => s.trim()) : [],
          images,
          createdAt: serverTimestamp(),
        });
        setMessage("Produkt byl přidán.");
      }

      // Reset form
      setForm({
        name: "",
        price: "",
        description: "",
        category: "",
        subcategory: "",
        sizes: "",
        images: "",
      });
      setEditId(null);
      localStorage.removeItem("products"); // vymaž cache po změně
      window.location.reload();
    } catch (err) {
      console.error("Error:", err);
      setMessage("Nepodařilo se uložit produkt.");
    } finally {
      setLoading(false);
    }
  };

  // Fill form for edit
  const handleEdit = (product) => {
    setForm({
      name: product.name,
      price: product.price,
      description: product.description || "",
      category: product.category,
      subcategory: product.subcategory || "",
      sizes: product.sizes ? product.sizes.join(",") : "",
      images: product.images ? product.images.join(",") : "",
    });
    setEditId(product.id);
  };

  // Delete product
  const handleDelete = async (id) => {
    if (window.confirm("Opravdu chcete produkt smazat?")) {
      await deleteDoc(doc(db, "products", id));
      setProducts(products.filter((p) => p.id !== id));
      setMessage("Produkt byl smazán.");
      localStorage.removeItem("products"); // vymaž cache po změně
    }
  };

  // Clear cache manually
  const handleClearCache = () => {
    localStorage.removeItem("products");
    setMessage("🗑️ Cache produktů byla vymazána.");
  };

  return (
    <main className="admin">
      <h1 className="admin__title">
        {editId ? "Upravit produkt" : "Přidat produkt"}
      </h1>

      {/* Form */}
      <form className="admin__form" onSubmit={handleSubmit}>
        <label>
          Název:
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            required
          />
        </label>

        <label>
          Cena (Kč):
          <input
            type="number"
            name="price"
            value={form.price}
            onChange={handleChange}
            required
          />
        </label>

        <label>
          Popis:
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
          />
        </label>

        <label>
          Kategorie:
          <select
            name="category"
            value={form.category}
            onChange={handleChange}
            required
          >
            <option value="">-- Vyber kategorii --</option>
            <option value="merch">Merch</option>
            <option value="produkty">Produkty Codaco</option>
          </select>
        </label>

        {form.category === "merch" && (
          <label>
            Podkategorie:
            <select
              name="subcategory"
              value={form.subcategory}
              onChange={handleChange}
              required
            >
              <option value="">-- Vyber podkategorii --</option>
              <option value="kratky-rukav">Krátký rukáv</option>
              <option value="dlouhy-rukav">Dlouhý rukáv</option>
              <option value="ostatni">Ostatní</option>
            </select>
          </label>
        )}

        <label>
          Velikosti (oddělit čárkou):
          <input
            type="text"
            name="sizes"
            value={form.sizes}
            onChange={handleChange}
          />
        </label>

        <label>
          URL obrázků (oddělit čárkou):
          <input
            type="text"
            name="images"
            value={form.images}
            onChange={handleChange}
          />
        </label>

        {/* Preview images */}
        {form.images && (
          <div className="admin__preview">
            {form.images.split(",").map((url, i) => {
              let cleanUrl = url.trim();
              if (
                cleanUrl.includes("github.com") &&
                cleanUrl.includes("/blob/")
              ) {
                cleanUrl = cleanUrl
                  .replace("github.com", "raw.githubusercontent.com")
                  .replace("/blob/", "/");
              }
              return (
                <img
                  key={i}
                  src={cleanUrl}
                  alt="Náhled"
                  className="admin__preview-image"
                />
              );
            })}
          </div>
        )}

        <button type="submit" disabled={loading}>
          {loading ? "Ukládám..." : editId ? "Uložit změny" : "Uložit produkt"}
        </button>
      </form>

      <button onClick={handleClearCache} className="admin__clear-cache">
        Vymazat cache produktů
      </button>

      {message && <p className="admin__message">{message}</p>}

      {/* Product list */}
      <h2 className="admin__subtitle">Seznam produktů</h2>
      <ul className="admin__list">
        {products.map((p) => (
          <li key={p.id} className="admin__item">
            <div className="admin__item-info">
              {p.images && p.images.length > 0 && (
                <img
                  src={p.images[0]}
                  alt={p.name}
                  className="admin__item-thumb"
                />
              )}
              <span>
                {p.name} – {p.price} Kč {" | "}
                {p.category}
                {p.subcategory && ` / ${p.subcategory}`}
              </span>
            </div>
            <div className="admin__item-actions">
              <button onClick={() => handleEdit(p)}>Upravit</button>
              <button onClick={() => handleDelete(p.id)}>Smazat</button>
            </div>
          </li>
        ))}
      </ul>
    </main>
  );
}
